/**
 * product-card--unified.js
 *
 * Extends Prestige's native ProductCard behaviour to update title, price,
 * and quick-buy modal when a sibling-product color swatch is selected.
 *
 * Works alongside Prestige's existing theme.js which already handles:
 *   - Image swapping (data-variant-media)
 *   - Link href updates (data-product-url)
 *
 * This file handles everything else:
 *   - Product title text + href
 *   - Price (sale-price / compare-at-price elements)
 *   - Promo text (Get this for $X limited time only), driven by sibling.promo_pct
 *   - Badges (on-sale-badge, sold-out-badge)
 *   - Quick buy modal handle + aria-controls
 *   - Age context buttons (show only contexts with 2+ images for active swatch)
 *   - Product link age_context param preservation on card age context click
 */

document.addEventListener('change', function (e) {
  const input = e.target;

  // Only handle radio inputs inside a unified product card
  if (input.type !== 'radio') return;

  const card = input.closest('product-card[data-siblings]');
  if (!card) return;

  let siblings;
  try {
    siblings = JSON.parse(card.getAttribute('data-siblings'));
  } catch (err) {
    console.error('product-card--unified: could not parse data-siblings', err);
    return;
  }

  // Find the sibling that matches the clicked input by input id
  const inputId = input.id;
  const sibling = siblings.find(s => s.input_id === inputId);
  if (!sibling) return;

  // --- Update product title ---
  const titleEl = card.querySelector('.product-title');
  if (titleEl) {
    titleEl.textContent = sibling.title;
    titleEl.href = sibling.url;
  }

  // --- Update price ---
  const priceList = card.querySelector('price-list');
  if (priceList) {
    const salePriceEl = priceList.querySelector('sale-price');
    const compareAtPriceEl = priceList.querySelector('compare-at-price');

    if (salePriceEl) {
      const isOnSale = sibling.compare_at_price > sibling.current_price;
      salePriceEl.className = salePriceEl.className
        .replace('text-on-sale', '')
        .replace('text-subdued', '')
        .trim();
      if (isOnSale) salePriceEl.classList.add('text-on-sale');

      const srOnly = salePriceEl.querySelector('.sr-only');
      salePriceEl.textContent = '';
      if (srOnly) salePriceEl.appendChild(srOnly);
      salePriceEl.appendChild(document.createTextNode(sibling.price));
    }

    if (sibling.compare_at_price > sibling.current_price) {
      const formattedCompare = formatMoney(sibling.compare_at_price);

      if (compareAtPriceEl) {
        const srOnly = compareAtPriceEl.querySelector('.sr-only');
        compareAtPriceEl.textContent = '';
        if (srOnly) compareAtPriceEl.appendChild(srOnly);
        compareAtPriceEl.appendChild(document.createTextNode(formattedCompare));
        compareAtPriceEl.hidden = false;
      } else {
        const newCompare = document.createElement('compare-at-price');
        newCompare.className = salePriceEl ? salePriceEl.className.replace('text-on-sale', 'text-subdued line-through') : 'text-subdued line-through';
        newCompare.innerHTML = `<span class="sr-only">${window.theme?.strings?.regularPrice || 'Regular price'}</span>${formattedCompare}`;
        priceList.appendChild(newCompare);
      }
    } else {
      if (compareAtPriceEl) {
        compareAtPriceEl.hidden = true;
      }
    }
  }

  // --- Update promo text ---
  syncPromoText(card, sibling);

  // --- Update badges ---
  const isOnSale = sibling.compare_at_price > sibling.current_price;

  let badgeList = card.querySelector('badge-list');

  if (!badgeList) {
    const figure = card.querySelector('.product-card__figure');
    if (figure) {
      badgeList = document.createElement('badge-list');
      badgeList.className = 'badge-list badge-list--vertical';
      figure.prepend(badgeList);
    }
  }

  if (badgeList) {
    badgeList.querySelectorAll('on-sale-badge, sold-out-badge').forEach(el => el.remove());

    if (!sibling.available) {
      const badge = document.createElement('sold-out-badge');
      badge.className = 'badge badge--sold-out';
      badge.textContent = 'Sold out';
      badgeList.appendChild(badge);
    } else if (isOnSale) {
      const pct = Math.round((1 - sibling.current_price / sibling.compare_at_price) * 100);
      const badge = document.createElement('on-sale-badge');
      badge.className = 'badge badge--on-sale';
      badge.textContent = `Save ${pct}%`;
      badgeList.appendChild(badge);
    }
  }

  // --- Update quick buy modal ---
  const quickBuyModal = card.querySelector('quick-buy-modal');
  if (quickBuyModal) {
    const currentHandle = quickBuyModal.getAttribute('handle') || '';
    const variantParam = currentHandle.split('?variant=')[1] || '';
    quickBuyModal.setAttribute('handle', `${sibling.url.split('?')[0].replace(/.*\/products\//, '/products/')}?variant=${variantParam}`);
  }

  // --- Update all remaining product links ---
  card.querySelectorAll('a[data-instant]').forEach(link => {
    link.href = sibling.url;
  });

  // --- Reset age context buttons and re-sync visibility for new swatch ---
  card.querySelectorAll('[data-card-age-context]').forEach(b => b.classList.remove('is-active'));
  syncAgeContextButtons(card);
  updateCardLinks(card, null);
});

/**
 * Format a Shopify price integer (cents) to a money string. Do not show cents
 */
function formatMoney(cents) {
  const moneyFormat = window.themeVariables?.moneyFormat || '${{amount}}';

  // Convert to dollars first and round immediately
  const dollars = Math.round(cents / 100);

  const amount = dollars.toString();

  const amountWithComma = dollars.toString().replace('.', ',');

  if (window.Shopify && window.Shopify.formatMoney) {
    return window.Shopify.formatMoney(dollars * 100, moneyFormat)
      .replace(/\.00(?=\s*(?:[A-Z]{3})?$)/, '')
      .replace(/,00(?=\s*(?:[A-Z]{3})?$)/, '');
  }

  return moneyFormat
    .replace('{{amount}}', amount)
    .replace('{{amount_no_decimals}}', amount)
    .replace('{{amount_with_comma_separator}}', amountWithComma);
}

/**
 * Sync the "Get this for $X limited time only" promo line to match the
 * given sibling's promo_pct. Creates the element if it doesn't exist yet,
 * removes it if the sibling has no active promo.
 *
 * sibling.current_price is in cents, same as the rest of the sibling payload.
 */
function syncPromoText(card, sibling) {
  let promoEl = card.querySelector('[data-card-promo-text]');
  const promoPct = sibling.promo_pct || 0;

  if (promoPct > 0) {
    const remainingPct = 100 - promoPct;
    const promoPrice = Math.round(sibling.current_price * remainingPct / 100);
    const promoText = `Get this for ${formatMoney(promoPrice)} limited time only`;

    if (promoEl) {
      promoEl.textContent = promoText;
    } else {
      const priceList = card.querySelector('price-list');
      if (priceList) {
        promoEl = document.createElement('p');
        promoEl.className = 'promo-text';
        promoEl.setAttribute('data-card-promo-text', '');
        promoEl.textContent = promoText;
        priceList.insertAdjacentElement('afterend', promoEl);
      }
    }
  } else if (promoEl) {
    promoEl.textContent = '\u00A0'; // keep line-height, matches Liquid's &nbsp; fallback
    promoEl.style.visibility = 'hidden';
  }
}

/**
 * Show only age context buttons that have 2+ images (primary + secondary)
 * for the currently selected swatch on this card.
 */
function syncAgeContextButtons(card) {
  const activeInput = card.querySelector('input[type="radio"]:checked');
  const filterEl = card.querySelector('[data-card-age-context-filter]');
  if (!filterEl) return;

  const buttons = filterEl.querySelectorAll('[data-card-age-context]');

  if (!activeInput) {
    buttons.forEach(b => b.style.display = '');
    filterEl.style.display = '';
    return;
  }

  const ageMediaRaw = activeInput.getAttribute('data-age-media');
  if (!ageMediaRaw) {
    buttons.forEach(b => b.style.display = '');
    filterEl.style.display = '';
    return;
  }

  let ageMedia;
  try {
    ageMedia = JSON.parse(ageMediaRaw);
  } catch (e) {
    buttons.forEach(b => b.style.display = '');
    filterEl.style.display = '';
    return;
  }

  buttons.forEach(btn => {
    const ctx = btn.getAttribute('data-card-age-context');
    const ctxData = ageMedia[ctx];
    const hasTwo = ctxData && ctxData.primary && ctxData.secondary;
    btn.style.display = hasTwo ? '' : 'none';
  });

  const anyVisible = Array.from(buttons).some(b => b.style.display !== 'none');
  filterEl.style.display = anyVisible ? '' : 'none';
}

/**
 * Update all product links on the card to include or remove the age_context param.
 * Pass null to remove the param (revert to base collection age context).
 */
function updateCardLinks(card, ctx) {
  card.querySelectorAll('a[data-instant], .product-card__media').forEach(link => {
    if (!link.href) return;
    try {
      const url = new URL(link.href, window.location.origin);
      if (ctx) {
        url.searchParams.set('age_context', ctx);
      } else {
        const activeInput = card.querySelector('input[type="radio"]:checked');
        const baseUrl = activeInput ? new URL(activeInput.getAttribute('data-product-url'), window.location.origin) : null;
        const baseCtx = baseUrl ? baseUrl.searchParams.get('age_context') : null;
        if (baseCtx) {
          url.searchParams.set('age_context', baseCtx);
        } else {
          url.searchParams.delete('age_context');
        }
      }
      link.href = url.toString();
    } catch (e) {
      // ignore malformed URLs
    }
  });
}

// Age context button click handler
document.addEventListener('click', function (e) {
  const btn = e.target.closest('[data-card-age-context]');
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const card = btn.closest('product-card');
  if (!card) return;

  const ctx = btn.getAttribute('data-card-age-context');
  const isActive = btn.classList.contains('is-active');

  card.querySelectorAll('[data-card-age-context]').forEach(b => b.classList.remove('is-active'));
  if (!isActive) btn.classList.add('is-active');

  const activeInput = card.querySelector('input[type="radio"]:checked');
  if (!activeInput) return;

  const ageMediaRaw = activeInput.getAttribute('data-age-media');
  if (!ageMediaRaw) return;

  let ageMedia;
  try {
    ageMedia = JSON.parse(ageMediaRaw);
  } catch (err) {
    console.warn('[card-age-context] Could not parse data-age-media', err);
    return;
  }

  const contextData = isActive ? null : ageMedia[ctx];

  if (!contextData) {
    const originalPrimary = activeInput.getAttribute('data-variant-media');
    const originalSecondary = activeInput.getAttribute('data-variant-secondary-media');
    swapCardImages(
      card,
      originalPrimary ? JSON.parse(originalPrimary) : null,
      originalSecondary ? JSON.parse(originalSecondary) : null
    );
    updateCardLinks(card, null);
    return;
  }

  swapCardImages(card, contextData.primary, contextData.secondary || null);
  updateCardLinks(card, isActive ? null : ctx);
});

function swapCardImages(card, primaryMedia, secondaryMedia) {
  const primaryImg = card.querySelector('.product-card__image--primary');
  const secondaryImg = card.querySelector('.product-card__image--secondary');

  if (primaryImg && primaryMedia) {
    const src = primaryMedia.preview_image.src;
    const w = primaryMedia.preview_image.width;
    const h = primaryMedia.preview_image.height;
    primaryImg.src = src + '&width=800';
    primaryImg.srcset = `${src}&width=200 200w, ${src}&width=400 400w, ${src}&width=600 600w, ${src}&width=800 800w, ${src}&width=1000 1000w`;
    primaryImg.width = w;
    primaryImg.height = h;
    primaryImg.alt = primaryMedia.alt || '';
  }

  if (secondaryImg && secondaryMedia) {
    const src = secondaryMedia.preview_image.src;
    const w = secondaryMedia.preview_image.width;
    const h = secondaryMedia.preview_image.height;
    secondaryImg.src = src + '&width=800';
    secondaryImg.srcset = `${src}&width=200 200w, ${src}&width=400 400w, ${src}&width=600 600w, ${src}&width=800 800w, ${src}&width=1000 1000w`;
    secondaryImg.width = w;
    secondaryImg.height = h;
    secondaryImg.alt = secondaryMedia.alt || '';
  }
}

/**
 * Initialise a single product card — syncs age context buttons, price, and badges
 * to match the pre-checked swatch. Stamped with data-unified-init to avoid
 * double-running.
 */
function initCard(card) {
  if (card.hasAttribute('data-unified-init')) return;
  card.setAttribute('data-unified-init', '');

  syncAgeContextButtons(card);

  if (!card.hasAttribute('data-siblings')) return;

  const checkedInput = card.querySelector('input[type="radio"]:checked');
  if (!checkedInput) return;

  let siblings;
  try {
    siblings = JSON.parse(card.getAttribute('data-siblings'));
  } catch (err) {
    return;
  }

  const sibling = siblings.find(s => s.input_id === checkedInput.id);
  if (!sibling) return;

  // --- Price ---
  const priceList = card.querySelector('price-list');
  if (priceList) {
    const salePriceEl = priceList.querySelector('sale-price');
    const compareAtPriceEl = priceList.querySelector('compare-at-price');
    const isOnSale = sibling.compare_at_price > sibling.current_price;

    if (salePriceEl) {
      salePriceEl.className = salePriceEl.className
        .replace('text-on-sale', '')
        .replace('text-subdued', '')
        .trim();
      if (isOnSale) salePriceEl.classList.add('text-on-sale');

      const srOnly = salePriceEl.querySelector('.sr-only');
      salePriceEl.textContent = '';
      if (srOnly) salePriceEl.appendChild(srOnly);
      salePriceEl.appendChild(document.createTextNode(sibling.price));
    }

    if (!isOnSale && compareAtPriceEl) {
      compareAtPriceEl.hidden = true;
    }
  }

  // --- Promo text ---
  syncPromoText(card, sibling);

  // --- Badges ---
  const isOnSale = sibling.compare_at_price > sibling.current_price;

  let badgeList = card.querySelector('badge-list');
  if (!badgeList) {
    const figure = card.querySelector('.product-card__figure');
    if (figure) {
      badgeList = document.createElement('badge-list');
      badgeList.className = 'badge-list badge-list--vertical';
      figure.prepend(badgeList);
    }
  }

  if (badgeList) {
    badgeList.querySelectorAll('on-sale-badge, sold-out-badge').forEach(el => el.remove());

    if (!sibling.available) {
      const badge = document.createElement('sold-out-badge');
      badge.className = 'badge badge--sold-out';
      badge.textContent = 'Sold out';
      badgeList.appendChild(badge);
    } else if (isOnSale) {
      const pct = Math.round((1 - sibling.current_price / sibling.compare_at_price) * 100);
      const badge = document.createElement('on-sale-badge');
      badge.className = 'badge badge--on-sale';
      badge.textContent = `Save ${pct}%`;
      badgeList.appendChild(badge);
    }
  }
}

// On page load — init all cards already in the DOM
document.querySelectorAll('product-card').forEach(card => initCard(card));

// Watch for predictive search results being injected into the DOM.
// Prestige adds a div.predictive-search__results container each time results arrive.
const observer = new MutationObserver(function (mutations) {
  mutations.forEach(function (mutation) {
    mutation.addedNodes.forEach(function (node) {
      if (node.nodeType !== 1) return;

      if (node.classList && node.classList.contains('predictive-search__results')) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            node.querySelectorAll('product-card').forEach(card => {
              card.removeAttribute('data-unified-init');
              initCard(card);
            });
          }, 0);
        });
      }
    });
  });
});

observer.observe(document.body, { childList: true, subtree: true });

/**
 * Exposes initCard globally so infinite-scroll.js can call it on cards
 * that are injected into the DOM after the initial page load.
 *
 * The existing MutationObserver in product-card--unified.js only watches
 * for predictive-search results; infinite scroll injects cards differently,
 * so we expose initCard directly instead of relying on the observer.
 */
window.initUnifiedCard = function (card) {
  // Remove the guard flag so initCard will run fresh on this card
  card.removeAttribute('data-unified-init');
  initCard(card);
};
